
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getChapterById, getChaptersByStoryId } from '@/services/stories';
import { generateAppLink, isNativePlatform, openInApp } from '@/utils/deep-links';

export const useChapterNavigation = (id: string | undefined) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [chapter, setChapter] = useState<any>(null);
  const [allChapters, setAllChapters] = useState<any[]>([]);
  const [nextChapter, setNextChapter] = useState<any>(null);
  const [prevChapter, setPrevChapter] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Determine if we're in preview mode
  const isPreview = location.pathname.includes('/preview/');

  useEffect(() => {
    const fetchChapterData = async () => {
      if (!id) return;
      
      setIsLoading(true);
      try {
        // Fetch the current chapter
        const { chapter: chapterData, error: chapterError } = await getChapterById(id);
        
        if (chapterError) {
          console.error("Error fetching chapter:", chapterError);
          setError(chapterError);
          setIsLoading(false);
          return;
        }
        
        if (chapterData) {
          // Enhanced logging for debugging author data issues
          console.log("Raw chapter data received:", chapterData);
          
          // Verify stories and authors nested objects
          const storyData = chapterData?.stories || {};
          console.log("Story data:", storyData);
          
          const authorData = storyData?.authors || {};
          console.log("Author data:", authorData);
          
          // Detailed author name logging
          const authorName = authorData?.pen_name || authorData?.full_name;
          console.log("Author name resolved:", {
            chapterId: chapterData.id,
            storyId: chapterData.story_id,
            storyTitle: storyData?.title,
            authorInfo: authorData,
            authorName: authorName || 'Author name missing',
            penName: authorData?.pen_name,
            fullName: authorData?.full_name
          });
          
          setChapter(chapterData);
          
          // Fetch all chapters for this story to find the next chapter
          const { chapters: chaptersData, error: chaptersError } = await getChaptersByStoryId(chapterData.story_id.toString());
          
          if (chaptersError) {
            console.error("Error fetching chapters:", chaptersError);
          } else if (chaptersData) {
            setAllChapters(chaptersData);
            // Find the next chapter (if any)
            const currentChapterIndex = chaptersData.findIndex(ch => ch.id === chapterData.id);
            if (currentChapterIndex !== -1 && currentChapterIndex < chaptersData.length - 1) {
              setNextChapter(chaptersData[currentChapterIndex + 1]);
            } else {
              setNextChapter(null);
            }
            
            // Find the previous chapter (if any)
            if (currentChapterIndex > 0) {
              setPrevChapter(chaptersData[currentChapterIndex - 1]);
            } else {
              setPrevChapter(null);
            }
          }
        } else {
          setError("Chapter not found");
        }
      } catch (err) {
        console.error("Unexpected error:", err);
        setError("An unexpected error occurred");
      } finally {
        setIsLoading(false);
      }
    };

    fetchChapterData();
  }, [id]);

  const handleNextChapter = () => {
    if (nextChapter) {
      const path = isPreview ? `/preview/chapter/${nextChapter.id}` : `/chapter/${nextChapter.id}`;
      navigate(path);
    }
  };

  const handlePrevChapter = () => {
    if (prevChapter) {
      const path = isPreview ? `/preview/chapter/${prevChapter.id}` : `/chapter/${prevChapter.id}`;
      navigate(path);
    }
  };

  const handleChapterSelect = (chapterId: string) => {
    const path = isPreview ? `/preview/chapter/${chapterId}` : `/chapter/${chapterId}`;
    navigate(path);
  };
  
  // Generate shareable link for the current chapter (deep linkable)
  const getShareableLink = () => {
    if (!chapter) return '';
    
    // Generate universal link for sharing
    return generateAppLink(`/chapter/${id}`);
  };

  return {
    chapter,
    allChapters,
    nextChapter,
    prevChapter,
    isLoading,
    error,
    handleNextChapter,
    handlePrevChapter,
    handleChapterSelect,
    getShareableLink,
    isPreview
  };
};
